import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Textarea } from './ui/textarea';
import {
  Calendar, Clock, Globe, FileText, Image as ImageIcon,
  CheckCircle2, XCircle, MessageSquare, Loader2
} from 'lucide-react';
import { approvalAPI } from '../lib/api';
import { formatDateLong, formatDateTimeLong } from '../lib/dateFormat';
import { toast } from 'sonner';

const statusColors = {
  draft: 'bg-secondary text-secondary-foreground',
  in_review: 'bg-amber-500/15 text-amber-400 border border-amber-500/20',
  approved: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20',
  scheduled: 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/20',
  published: 'bg-primary/15 text-teal-400 border border-primary/20',
  rejected: 'bg-rose-500/15 text-rose-400 border border-rose-500/20',
};

const platformIcons = {
  facebook: { color: '#1877F2', label: 'Facebook' },
  instagram: { color: '#E1306C', label: 'Instagram' },
  linkedin: { color: '#0A66C2', label: 'LinkedIn' },
  youtube: { color: '#FF0000', label: 'YouTube' },
  pinterest: { color: '#E60023', label: 'Pinterest' },
  gmb: { color: '#1A73E8', label: 'Google My Business' },
  substack: { color: '#FF6719', label: 'Substack' },
};

export function PostDetailSheet({ post, open, onOpenChange, onStatusChange }) {
  const [comment, setComment] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [showCommentBox, setShowCommentBox] = useState(false);

  if (!post) return null;

  const scheduledDate = post.scheduled_at
    ? new Date(post.scheduled_at)
    : null;
  const createdDate = post.created_at
    ? new Date(post.created_at)
    : null;

  const canApprove = ['in_review', 'draft', 'scheduled'].includes(post.status);
  const canReject = ['in_review', 'draft', 'scheduled'].includes(post.status);

  const handleAction = async (action) => {
    setActionLoading(action);
    try {
      // Find or create an approval for this post
      if (post.approval_id) {
        await approvalAPI.action(post.approval_id, { action, comment: comment || `Post ${action}d` });
      } else {
        // Submit for approval first, then act on it
        const submitRes = await approvalAPI.submit({
          workspace_id: post.workspace_id,
          post_id: post.id,
          notes: comment || `Post ${action}d directly`,
        });
        const approvalId = submitRes.data?.id;
        if (approvalId) {
          await approvalAPI.action(approvalId, { action, comment: comment || `Post ${action}d` });
        }
      }
      toast.success(action === 'approve' ? 'Post approved!' : 'Post rejected');
      setComment('');
      setShowCommentBox(false);
      if (onStatusChange) onStatusChange(action === 'approve' ? 'approved' : 'rejected');
      onOpenChange(false);
    } catch (err) {
      toast.error(`Failed to ${action} post: ${err.response?.data?.detail || err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[420px] sm:w-[540px] overflow-y-auto" data-testid="post-detail-sheet">
        <SheetHeader className="pb-4">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-lg font-display">{post.title || 'Untitled Post'}</SheetTitle>
            <Badge className={`text-xs ${statusColors[post.status] || 'bg-secondary'}`} data-testid="post-detail-status">
              {(post.status || 'draft').replace('_', ' ')}
            </Badge>
          </div>
        </SheetHeader>

        <div className="space-y-5">
          {/* Timing Section */}
          <div className="space-y-3" data-testid="post-detail-timing">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Schedule</h3>
            {scheduledDate ? (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/40">
                <div className="h-9 w-9 rounded-lg bg-cyan-500/15 flex items-center justify-center">
                  <Calendar className="h-4 w-4 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm font-medium">{formatDateLong(post.scheduled_at)}</p>
                  <p className="text-xs text-muted-foreground">{scheduledDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/40">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Not scheduled yet</p>
              </div>
            )}
            {createdDate && (
              <p className="text-xs text-muted-foreground">
                Created: {formatDateTimeLong(post.created_at)}
              </p>
            )}
          </div>

          <Separator />

          {/* Platforms Section */}
          <div className="space-y-3" data-testid="post-detail-platforms">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Platforms</h3>
            <div className="flex flex-wrap gap-2">
              {(post.platforms || []).length > 0 ? (
                post.platforms.map(p => {
                  const info = platformIcons[p] || { color: '#888', label: p };
                  return (
                    <Badge key={p} variant="outline" className="text-xs gap-1.5 py-1 px-2.5">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: info.color }} />
                      {info.label}
                    </Badge>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground">No platforms selected</p>
              )}
            </div>
          </div>

          <Separator />

          {/* Caption / Content Section */}
          <div className="space-y-3" data-testid="post-detail-content">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Caption / Content</h3>
            <div className="p-4 rounded-lg bg-secondary/40 min-h-[120px]">
              {post.content ? (
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{post.content}</p>
              ) : (
                <p className="text-sm text-muted-foreground italic">No content added yet.</p>
              )}
            </div>
          </div>

          {/* Media Attachments */}
          {(post.media_ids || []).length > 0 && (
            <>
              <Separator />
              <div className="space-y-3" data-testid="post-detail-media">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Media Attachments ({post.media_ids.length})
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {post.media_ids.map((id, i) => (
                    <div key={id} className="aspect-square rounded-lg bg-secondary/60 flex items-center justify-center">
                      <ImageIcon className="h-5 w-5 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <Separator />

          {/* Approve / Reject Section */}
          <div className="space-y-3" data-testid="post-approval-section">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Review & Approve</h3>

            {post.status === 'approved' && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                <p className="text-sm text-emerald-400">This post has been approved</p>
              </div>
            )}
            {post.status === 'rejected' && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/20">
                <XCircle className="h-4 w-4 text-rose-400" />
                <p className="text-sm text-rose-400">This post was rejected</p>
              </div>
            )}
            {post.status === 'published' && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/20">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <p className="text-sm">This post has been published</p>
              </div>
            )}

            {(canApprove || canReject) && (
              <div className="space-y-3">
                {/* Comment toggle */}
                {!showCommentBox ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground"
                    onClick={() => setShowCommentBox(true)}
                    data-testid="add-comment-toggle"
                  >
                    <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                    Add a comment (optional)
                  </Button>
                ) : (
                  <Textarea
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    placeholder="Add feedback or notes about this post..."
                    className="text-sm min-h-[80px]"
                    data-testid="approval-comment-input"
                  />
                )}

                {/* Action Buttons */}
                <div className="flex gap-2">
                  {canApprove && (
                    <Button
                      onClick={() => handleAction('approve')}
                      disabled={!!actionLoading}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="approve-post-button"
                    >
                      {actionLoading === 'approve' ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                      )}
                      Approve
                    </Button>
                  )}
                  {canReject && (
                    <Button
                      onClick={() => handleAction('reject')}
                      disabled={!!actionLoading}
                      variant="outline"
                      className="flex-1 border-rose-500/30 text-rose-400 hover:bg-rose-500/10"
                      data-testid="reject-post-button"
                    >
                      {actionLoading === 'reject' ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <XCircle className="h-4 w-4 mr-2" />
                      )}
                      Reject
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
